import useSWR from 'swr';
import { isAxiosError } from 'axios';
import { getContractDraftSourceUrl } from '@/constant/approval';
import type { ContractDraftSource } from '@/types/approval';
import { getNestedDataFetcher } from '@/utils/apis';

/**
 * draft-source 실패 사유. 셋 다 재시도해도 결과가 같은 확정 응답이라 호출부가 문구만 고르면 됨.
 * - forbidden-contract: 404 — 접근 불가 계약이 하나라도 섞이면 전체 거절
 * - invalid-request: 422 — 작성자 사번 없음 또는 contractIds 결합값 100자 초과
 * - elaw-unavailable: 424 — 법틀 연동 장애
 */
export type ContractDraftSourceErrorKind =
  | 'forbidden-contract'
  | 'invalid-request'
  | 'elaw-unavailable'
  | 'unknown';

export const getContractDraftSourceErrorKind = (
  error: unknown,
): ContractDraftSourceErrorKind | undefined => {
  if (!error) return undefined;
  const status = isAxiosError(error) ? error.response?.status : undefined;
  switch (status) {
    case 404:
      return 'forbidden-contract';
    case 422:
      return 'invalid-request';
    case 424:
      return 'elaw-unavailable';
    default:
      return 'unknown';
  }
};

const isTerminalClientError = (error: unknown): boolean => {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  return status === 404 || status === 422 || status === 424;
};

/**
 * 계약 품의(117·118) 작성 사전 데이터 — 법틀 계약 목록.
 * - contractIds 생략(undefined) → 선택 가능 계약 전건(선택 목록용)
 * - contractIds 배열 → 그 계약만 요청 순서대로(선택 확정·조회 재조회용). 빈 배열은 호출 skip
 * - code 없으면 호출 skip
 * - partial → 계약별 권한 결과(contractResults). 조회 화면 원문 링크용
 * accessLink 가 만료되는 링크라 포커스 재검증은 끄고 mount 때 받은 값을 유지.
 */
const useContractDraftSource = (
  code?: number | null,
  contractIds?: number[],
  options?: { partial?: boolean },
) => {
  const skip =
    typeof code !== 'number' ||
    (Array.isArray(contractIds) && contractIds.length === 0);
  const key = skip
    ? null
    : getContractDraftSourceUrl(code, contractIds, options);

  const { data, error, isLoading, isValidating, mutate } =
    useSWR<ContractDraftSource>(key, getNestedDataFetcher, {
      revalidateOnFocus: false,
      shouldRetryOnError: (err) => !isTerminalClientError(err),
    });

  return {
    data,
    contracts: data?.display.contracts,
    contractResults: data?.display.contractResults,
    error,
    errorKind: getContractDraftSourceErrorKind(error),
    isLoading,
    isValidating,
    mutate,
  };
};

export default useContractDraftSource;
